import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Input, Select, Tag, Image, Space, Button, message } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import dayjs from 'dayjs';
import BackButton from '../../components/BackButton';
import petService from '../../services/petService';

const { Option } = Select;

const AdminAnimalsList = () => {
  const { t, i18n } = useTranslation();
  const [animals, setAnimals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    animal_category: null,
    size: null
  });

  useEffect(() => {
    fetchCategories();
    fetchAnimals();
  }, []);

  const fetchCategories = async () => {
    const result = await petService.fetchAnimalCategories();
    if (result.success) {
      setCategories(result.data.results || result.data);
    }
  };

  const fetchAnimals = async () => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.animal_category) params.animal_category = filters.animal_category;
    if (filters.size) params.size = filters.size;

    const result = await petService.adminFetchAllPets(params);
    if (result.success) {
      setAnimals(result.data.results || result.data);
    } else {
      message.error(result.error || t('admin.animals.loadFailed'));
    }
    setLoading(false);
  };

  const handleSearch = () => {
    fetchAnimals();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', animal_category: null, size: null });
    setTimeout(() => fetchAnimals(), 0);
  };

  const getSizeLabel = (size) => {
    const sizeLabels = {
      SMALL: t('pets.addEdit.sizes.small'),
      MEDIUM: t('pets.addEdit.sizes.medium'),
      LARGE: t('pets.addEdit.sizes.large'),
      EXTRA_LARGE: t('pets.addEdit.sizes.extraLarge')
    };
    return sizeLabels[size] || size;
  };

  const getSizeColor = (size) => {
    const colors = {
      SMALL: 'blue',
      MEDIUM: 'green',
      LARGE: 'orange',
      EXTRA_LARGE: 'red'
    };
    return colors[size] || 'default';
  };

  const getImageUrl = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'object') {
      return photo.thumbnail || photo.medium || photo.full_size;
    }
    return photo;
  };

  const handleExport = () => {
    const exportColumns = [
      { title: t('common.name'), dataIndex: 'name' },
      { title: t('admin.animals.type'), dataIndex: 'animal_category_name' },
      { title: t('admin.animals.breed'), dataIndex: 'breed_name' },
      {
        title: t('admin.animals.size'),
        dataIndex: 'size',
        exportRender: (size) => getSizeLabel(size)
      },
      {
        title: t('admin.animals.age'),
        dataIndex: 'age',
        exportRender: (age) => age !== null ? `${age}` : ''
      },
      { title: t('admin.animals.ownerName'), dataIndex: 'owner_name' },
      { title: t('admin.animals.ownerEmail'), dataIndex: 'owner_email' },
      { title: t('admin.animals.microchip'), dataIndex: 'microchip_code' },
      {
        title: t('admin.animals.added'),
        dataIndex: 'created_at',
        exportRender: (date) => dayjs(date).format('YYYY-MM-DD')
      }
    ];
    exportToExcel(animals, exportColumns, 'animals', t('admin.animals.title'));
    message.success(t('admin.animals.exportSuccess'));
  };

  const columns = [
    {
      title: t('admin.animals.photo'),
      dataIndex: 'photo',
      key: 'photo',
      width: 70,
      render: (photo) => {
        const url = getImageUrl(photo);
        return url ? (
          <Image
            src={url}
            alt={t('admin.animals.animalAlt')}
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <div style={{ width: 50, height: 50, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
            N/A
          </div>
        );
      }
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: t('admin.animals.type'),
      dataIndex: 'animal_category_name',
      key: 'animal_category_name',
      render: (name) => <Tag color="blue">{getAnimalNameWithEmoji(name)}</Tag>
    },
    {
      title: t('admin.animals.breed'),
      dataIndex: 'breed_name',
      key: 'breed_name',
      render: (breed) => breed || <span style={{ color: '#999' }}>-</span>
    },
    {
      title: t('admin.animals.size'),
      dataIndex: 'size',
      key: 'size',
      render: (size) => size ? <Tag color={getSizeColor(size)}>{getSizeLabel(size)}</Tag> : '-'
    },
    {
      title: t('admin.animals.age'),
      dataIndex: 'age',
      key: 'age',
      render: (age) => age !== null ? t('admin.animals.ageYears', { count: age }) : '-'
    },
    {
      title: t('admin.animals.owner'),
      key: 'owner',
      render: (_, record) => (
        <div>
          <div>{record.owner_name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.owner_email}</div>
        </div>
      )
    },
    {
      title: t('admin.animals.microchip'),
      dataIndex: 'microchip_code',
      key: 'microchip_code',
      render: (code) => code || <span style={{ color: '#999' }}>-</span>
    },
    {
      title: t('admin.animals.added'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />

      <Card
        title={t('admin.animals.titleWithCount', { count: animals.length })}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!animals.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchAnimals}>
              {t('common.refresh')}
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('admin.animals.description')}
        </p>

        {/* Filters */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder={t('admin.animals.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder={t('admin.animals.filterByType')}
            value={filters.animal_category}
            onChange={(value) => handleFilterChange('animal_category', value)}
            style={{ width: 150 }}
            allowClear
          >
            {categories.map(cat => (
              <Option key={cat.id} value={cat.id}>{getAnimalNameWithEmoji(i18n.language === 'el' ? (cat.name_el || cat.name_en) : cat.name_en)}</Option>
            ))}
          </Select>
          <Select
            placeholder={t('admin.animals.filterBySize')}
            value={filters.size}
            onChange={(value) => handleFilterChange('size', value)}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="SMALL">{t('pets.addEdit.sizes.small')}</Option>
            <Option value="MEDIUM">{t('pets.addEdit.sizes.medium')}</Option>
            <Option value="LARGE">{t('pets.addEdit.sizes.large')}</Option>
            <Option value="EXTRA_LARGE">{t('pets.addEdit.sizes.extraLarge')}</Option>
          </Select>
          <Button type="primary" onClick={handleSearch}>
            {t('common.search')}
          </Button>
          <Button onClick={handleClearFilters}>
            {t('common.clear')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={animals}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('admin.animals.totalAnimals', { total })
          }}
        />
      </Card>
    </div>
  );
};

export default AdminAnimalsList;
